import { minutesSinceMidnightInTz, wallClockHourToUtcMs } from '@/lib/facilityTimezone';
import type { CalendarAppointment } from '@/services/appointments.service';

/**
 * Pure geometry for the scheduler's time-grid columns, extracted from
 * SchedulePage so it can be tested without mounting the page (which would pull
 * in React, react-query and the whole route tree). Follows the same convention
 * as `pages/tasks/tasksShared.ts` and `pages/payments/paymentsShared.ts`.
 *
 * Nothing here touches React or the DOM — only `Date`, `Math` and the pure
 * `minutesSinceMidnightInTz` helper — so its tests need no jsdom environment.
 */

export interface PositionedCalendarItem {
  appt: CalendarAppointment;
  topPct: number;
  heightPct: number;
  leftPct: number;
  widthPct: number;
}

export function overlapsInMinutes(a: CalendarAppointment, b: CalendarAppointment): boolean {
  return new Date(a.start).getTime() < new Date(b.end).getTime()
    && new Date(a.end).getTime() > new Date(b.start).getTime();
}

/**
 * Move one appointment to new times inside a cached calendar list.
 *
 * This is the optimistic write behind drag/resize: card geometry is a pure
 * function of `start` / `end`, so patching them in the react-query cache is all
 * it takes for the card to re-place itself and the lane widths to re-solve. The
 * caller passes the ISO strings the drag hook produced, which are already in the
 * server's own format (`YYYY-MM-DDTHH:mm:ssZ`), so the authoritative refetch that
 * follows will not make the card jump.
 *
 * `durationMinutes` is recomputed rather than left alone: a resize that changes
 * only the end time would otherwise leave the column's hour rollup and the detail
 * subtitle reading the pre-resize duration until the refetch lands.
 *
 * Returns the input untouched when there is nothing to patch, so a caller can use
 * it directly as a react-query updater.
 */
export function applyMoveToAppointments(
  list: CalendarAppointment[] | undefined,
  apptId: number,
  newStart: string,
  newEnd: string,
): CalendarAppointment[] | undefined {
  if (!list) return list;

  let changed = false;
  const next = list.map((appt) => {
    if (appt.id !== apptId) return appt;
    changed = true;

    const startMs = new Date(newStart).getTime();
    const endMs = new Date(newEnd).getTime();
    const durationMinutes = Number.isFinite(startMs) && Number.isFinite(endMs)
      ? Math.max(0, Math.round((endMs - startMs) / 60000))
      : appt.durationMinutes;

    return { ...appt, start: newStart, end: newEnd, durationMinutes };
  });

  // Preserve identity when nothing matched so react-query does not treat a
  // no-op as a change and re-render every column.
  return changed ? next : list;
}

/**
 * Legacy parity (scheduler-display-week-cell-tz.php 226-238): when the day is a
 * DST fall-back (wall clock gains an hour), the legacy grid subtracts 3600 from
 * `$totalTime` so the 25-hour day is still drawn as the configured window.
 * Conversely spring-forward days are 23 hours. We compute the REAL elapsed
 * minutes between startHour:00 and hourEnd:00 for the specific dateStr — this
 * is DST-aware via the platform's Date arithmetic. Falls back to the naive
 * wall-clock calc when dateStr is missing or unparseable.
 *
 * `tz` is the zone the transition is measured in, and it matters: the grid's
 * vertical positions are wall-clock in the *user's* zone, so the divisor has to
 * come from the same zone or a browser outside it lands the whole column an hour
 * off on the two transition days. Omitting `tz` measures in the browser's own
 * zone — the pre-tz behavior, kept for callers that have no user zone to pass.
 */
export function computeVisibleMinutes(
  startHour: number,
  hourEnd: number,
  dateStr?: string,
  tz?: string,
): number {
  const naive = Math.max(1, (hourEnd - startHour) * 60);
  if (!dateStr) return naive;
  if (tz) {
    // An explicit zone is answered in that zone or not at all: falling back to the
    // browser's would make the result depend on where the viewer happens to be,
    // which is the inconsistency this parameter exists to remove.
    const startMs = wallClockHourToUtcMs(dateStr, startHour, tz);
    const endMs = wallClockHourToUtcMs(dateStr, hourEnd, tz);
    if (startMs === null || endMs === null) return naive;
    const spanned = (endMs - startMs) / 60000;
    return spanned > 0 ? spanned : naive;
  }
  const parts = dateStr.split('-');
  if (parts.length !== 3) return naive;
  const [y, m, d] = parts.map((p) => Number(p));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return naive;
  const dayStart = new Date(y, m - 1, d, startHour, 0, 0, 0);
  // Math.floor(hourEnd) keeps overflow past midnight handled via Date rollover.
  const dayEnd = new Date(y, m - 1, d, hourEnd, 0, 0, 0);
  const diff = (dayEnd.getTime() - dayStart.getTime()) / 60000;
  return diff > 0 ? diff : naive;
}

/**
 * Place each appointment in a column: vertical position/height from its times,
 * horizontal lane/width from how many appointments it actually overlaps.
 *
 * Two rules are load-bearing and easy to break together:
 *
 *  - **Lane assignment uses the TRUE end time.** A previous attempt padded each
 *    span out to a full grid increment before assigning lanes, so appointments
 *    that do not overlap at all were treated as overlapping whenever the gap
 *    between them was under one increment. On the default 30-minute grid that
 *    halved the width of two back-to-back 15-minute appointments.
 *  - **Painted height has a floor of one increment** (`slotMinutes`), so a card
 *    shorter than the grid step still shows its text and hover affordances.
 *
 * Those two rules disagree by design: a sub-increment card is painted slightly
 * taller than its true span, which can overlap the next card's top edge. That is
 * the accepted trade — the alternative costs width for every short appointment.
 */
export function layoutEvents(
  events: CalendarAppointment[],
  startHour: number,
  hourEnd: number,
  laneStartPct = 0,
  laneWidthPct = 100,
  dateStr?: string,
  userTz?: string,
  slotMinutes?: number,
): PositionedCalendarItem[] {
  const visibleStart = startHour * 60;
  const visibleEnd = hourEnd * 60;
  const sorted = [...events].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const assigned = sorted.map((appt) => ({ appt, lane: 0 }));
  const active: Array<{ end: number; lane: number }> = [];

  // Wall-clock minutes-since-midnight in the user's TZ. When no userTz is
  // supplied we fall back to browser-local (Date.getHours), preserving
  // legacy behavior for any caller that hasn't been migrated.
  const minutesAt = (iso: string): number =>
    userTz ? minutesSinceMidnightInTz(iso, userTz)
           : (new Date(iso).getHours() * 60 + new Date(iso).getMinutes());

  for (const item of assigned) {
    const startMins = minutesAt(item.appt.start);
    const endMins = minutesAt(item.appt.end);

    for (let i = active.length - 1; i >= 0; i -= 1) {
      if (active[i].end <= startMins) active.splice(i, 1);
    }

    let lane = 0;
    while (active.some((entry) => entry.lane === lane)) lane += 1;
    item.lane = lane;
    active.push({ end: endMins, lane });
  }

  // P1-10: scale positioning by the day's real wall-clock duration so DST
  // transitions don't misposition events after 2 AM. Measured in the same zone
  // `minutesAt` uses, so the divisor and the positions agree.
  const totalMinutes = Math.max(1, computeVisibleMinutes(startHour, hourEnd, dateStr, userTz));

  return assigned.map(({ appt, lane }) => {
    const startMins = Math.max(visibleStart, minutesAt(appt.start));
    const endMins = Math.min(visibleEnd, minutesAt(appt.end));
    const overlapping = assigned.filter((entry) => overlapsInMinutes(entry.appt, appt));
    const laneCount = Math.max(1, ...overlapping.map((entry) => entry.lane + 1));
    const widthPct = laneWidthPct / laneCount;

    const rawHeightPct = ((endMins - startMins) / totalMinutes) * 100;
    const minHeightPct = slotMinutes ? (slotMinutes / totalMinutes) * 100 : 0;

    return {
      appt,
      topPct: ((startMins - visibleStart) / totalMinutes) * 100,
      heightPct: Math.max(rawHeightPct, minHeightPct),
      leftPct: laneStartPct + lane * widthPct,
      widthPct,
    };
  });
}

// ─── Mobile day view (SCRUM-1448) ─────────────────────────────────────────────

/**
 * The day grid's `grid-template-columns`.
 *
 * `SchedulePage.tsx` carried this as an inline template literal. It lives here so the desktop
 * string can be asserted byte-for-byte: at `compact === false` the output is identical to what
 * the page produced before this ticket, which is the whole desktop-invariance argument for the
 * grid half of SCRUM-1448 — a JS branch emits no CSS, so `assert-desktop-frozen.mjs` cannot
 * speak to it.
 *
 * `minmax(0, 1fr)` is what let every schedule column collapse: with the 224px filters rail in
 * flow, a 375px viewport left ~150px for a 4rem gutter plus N columns, so each schedule was a
 * few tens of pixels and its text broke one or two letters per line. A minimum of 9rem (144px)
 * makes a column readable and turns the overflow into a horizontal scroll, which the day-view
 * wrapper's existing `overflow-auto` already handles.
 */
export function dayGridTemplate(columnCount: number, compact: boolean): string {
  return `4rem repeat(${columnCount}, minmax(${compact ? '9rem' : '0'}, 1fr))`;
}

/**
 * The hover popup's width, capped to the viewport.
 *
 * The popup is a portalled `position: fixed` panel at a hard 400px, and that number feeds BOTH
 * the rendered `width` and the left-edge clamp in `computeCoords`. On a 375px screen a 400px
 * panel cannot fit however it is positioned — it overlapped the appointment columns and its
 * text wrapped one or two words per line. Clamping in one function keeps the rendered width and
 * the clamp arithmetic in agreement; two independent caps would park the panel off its anchor,
 * which is the trap already recorded for `Select`'s popover.
 *
 * 92vw rather than 100vw leaves the 8px edge gutter `computeCoords` already assumes on both
 * sides.
 */
export function popupWidth(max: number, viewportWidth: number): number {
  return Math.min(max, Math.round(viewportWidth * 0.92));
}

// ─── View mode in the URL ─────────────────────────────────────────────────────

export type ScheduleViewMode = 'week' | 'day' | 'month' | 'overview';

/** A `?view=` value the page can show, or null for a missing or unknown one. */
export function parseScheduleView(raw: string | null): ScheduleViewMode | null {
  return raw === 'week' || raw === 'day' || raw === 'month' || raw === 'overview' ? raw : null;
}

/**
 * The search params after the user picks a view inside the page.
 *
 * The sidebar's Day View / Week View rows are `/schedule?view=day|week` and light up only on an
 * exact query match, so the page has to write the view it switched to back into the URL — or the
 * URL and the highlighted row keep naming the view the user arrived on. `date` is dropped: it
 * only seeds the anchors on mount, is stale once the user moves, and would stop the row matching.
 *
 * Returns the input itself when it already says this view with no date, so the caller can skip a
 * navigation that would change nothing.
 */
export function withScheduleView(params: URLSearchParams, mode: ScheduleViewMode): URLSearchParams {
  if (params.get('view') === mode && !params.has('date')) return params;
  const next = new URLSearchParams(params);
  next.set('view', mode);
  next.delete('date');
  return next;
}

// ─── Toolbar date label (SCRUM-2136) ──────────────────────────────────────────

export interface ScheduleDateLabelText {
  /** What the toolbar has always shown, e.g. "Monday, October 5, 2026". */
  full: string;
  /** The same date in a third of the width, still naming the day and month: "Mon, 10/5". */
  short: string;
  /**
   * The short label of the widest dates this view can show. The label's minimum width is the
   * widest of these, so it is the same on every date: a minimum that followed `short` re-wrapped
   * the toolbar as the user paged from "Fri, 10/9" to "Wed, 10/14".
   */
  samples: string[];
}

/** "Mar 10" */
function shortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "3/10" */
function numericDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
}

/**
 * Dates whose short labels are the widest a view shows. The label sets `tabular-nums`, so every
 * digit is the same width and a two-digit month and day are as wide as a date gets; what still
 * varies is the weekday name, and in month view the month name, so each of those is sampled.
 * Built with the same formatters as the label, so they follow the user's locale.
 */
function shortLabelSamples(mode: ScheduleViewMode): string[] {
  if (mode === 'month') {
    return Array.from({ length: 12 }, (_, m) =>
      new Date(2026, m, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }));
  }
  if (mode === 'week') {
    return [`${numericDate(new Date(2025, 11, 22))} – ${numericDate(new Date(2025, 11, 28))}`];
  }
  // Mon 12/22/2025 – Sun 12/28/2025: every weekday, at a two-digit month and day.
  return Array.from({ length: 7 }, (_, i) =>
    new Date(2025, 11, 22 + i).toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' }));
}

/**
 * The toolbar's date label, in two lengths.
 *
 * The label gets whatever room the navigation, the saved-view controls and the view switcher
 * leave, and it used to `truncate`. In day view the weekday leads, so a crowded toolbar cut
 * "Monday, October 5, 2026" to "Monday, …" and the date itself was the part that disappeared.
 * `ScheduleDateLabel` shows `full` when it fits and `short` when it does not.
 *
 * `day` is optional because the page indexes `weekDays[dayOffset]`, which it has always guarded.
 */
export function scheduleDateLabelText(
  mode: ScheduleViewMode,
  anchors: { day: Date | undefined; weekStart: Date; weekEnd: Date; month: Date },
): ScheduleDateLabelText {
  const samples = shortLabelSamples(mode);
  if (mode === 'month') {
    return {
      full: anchors.month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      short: anchors.month.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
      samples,
    };
  }
  if (mode === 'week') {
    const { weekStart, weekEnd } = anchors;
    const startYear = weekStart.getFullYear();
    const endYear = weekEnd.getFullYear();
    return {
      // A week that crosses New Year names both years, or Dec 28 – Jan 3 reads as one year.
      full: startYear === endYear
        ? `${shortDate(weekStart)} – ${shortDate(weekEnd)}, ${startYear}`
        : `${shortDate(weekStart)}, ${startYear} – ${shortDate(weekEnd)}, ${endYear}`,
      short: `${numericDate(weekStart)} – ${numericDate(weekEnd)}`,
      samples,
    };
  }
  // day or overview
  const { day } = anchors;
  if (!day) return { full: '', short: '', samples };
  return {
    full: day.toLocaleDateString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    }),
    short: day.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' }),
    samples,
  };
}

/**
 * Which length of label to show, given the full label's natural width and the room it has.
 * `reserve` is room kept free after the text (the loading spinner's), so the full form is shown
 * only when it fits with that room to spare.
 *
 * A width of 0 means "not measured yet" — jsdom, and the moment before the first layout — and
 * reads as `full`, so the label never starts on the short form and then grows.
 */
export function pickScheduleDateLabel(fullWidth: number, slotWidth: number, reserve = 0): 'full' | 'short' {
  if (fullWidth === 0 || slotWidth === 0) return 'full';
  return fullWidth + reserve <= slotWidth ? 'full' : 'short';
}
